import React, { useState, useEffect } from "react"
import { Link } from "gatsby"
import { servicesApi, API_HOST } from "../../services/api"
import "./style.css"

const SERVICE_ORDER = [
  "branding",
  "design",
  "printing",
  "security",
  "marketing",
  "gifts",
  "development",
  "events",
]

function imgSrc(path) {
  if (!path) return null
  if (path.startsWith("http")) return path
  return `${API_HOST}${path}`
}

const cleanStr = (str) =>
  (str || "")
    .toLowerCase()
    .replace(/[-_]/g, " ")
    .trim()

const slugify = (str) =>
  (str || "")
    .toLowerCase()
    .trim()
    .replace(/&/g, "and")
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "")
    .replace(/-+/g, "-")

const getServiceTypeName = (service, lang = "en") => {
  if (service.service_type && typeof service.service_type === "object") {
    return lang === "ar"
      ? service.service_type.name_ar
      : service.service_type.name_en
  }

  if (typeof service.service_type === "string") {
    return service.service_type
  }

  return ""
}

const getServiceSortKey = (service) => {
  const typeName = getServiceTypeName(service, "en")
  const title = service.title_en || ""
  const joined = cleanStr(`${typeName} ${title}`)

  const matched = SERVICE_ORDER.find(key => joined.includes(key))
  return matched || joined
}

const getServiceTitle = (service, lang = "en") => {
  if (lang === "ar") {
    return service.title_ar || getServiceTypeName(service, "ar") || service.title_en || "Service"
  }

  return service.title_en || getServiceTypeName(service, "en") || service.title_ar || "Service"
}

const getServicePath = (service, lang = "en") => {
  const title = getServiceTitle(service, "en")
  const slug = slugify(title || `service-${service.id}`)

  return lang === "ar"
    ? `/ar/services/${service.id}-${slug}`
    : `/services/${service.id}-${slug}`
}

const ServicesBox = ({ lang, canNotFindWhatIamLookingForSection }) => {
  const [services, setServices] = useState([])
  const [loading, setLoading] = useState(true)

  const isAr = lang === "ar"
  const moreText = isAr ? "المزيد" : "Learn More"

  useEffect(() => {
    let cancelled = false

    servicesApi.index()
      .then(data => {
        if (cancelled) return

        const sorted = [...data].sort((a, b) => {
          const aKey = getServiceSortKey(a)
          const bKey = getServiceSortKey(b)

          const ia = SERVICE_ORDER.indexOf(aKey)
          const ib = SERVICE_ORDER.indexOf(bKey)

          if (ia !== -1 && ib !== -1) return ia - ib
          if (ia === -1 && ib !== -1) return 1
          if (ia !== -1 && ib === -1) return -1

          return aKey.localeCompare(bKey)
        })

        setServices(sorted)
        setLoading(false)
      })
      .catch(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [])

  if (loading) return null

  return (
    <div className="services-page">
      <div className="container">
        {services.map(service => {
          const title = getServiceTitle(service, lang)

          const description = isAr
            ? service.description_ar
            : service.description_en

          const coverImg =
            service.current_image_url ||
            service.image_urls?.[0] ||
            service.banner_image_url

          const path = getServicePath(service, lang)

          return (
            <div key={service.id} className="row mb-10">
              <div className="col-xs-12 col-sm-6 col-md-5">
                <div className="about-additional-img">
                  {coverImg ? (
                    <img
                      src={imgSrc(coverImg)}
                      alt={title}
                      className="img-responsive"
                      style={{ width: "100%", borderRadius: "inherit" }}
                    />
                  ) : (
                    <div
                      style={{
                        width: "100%",
                        aspectRatio: "4/3",
                        background: "linear-gradient(135deg, #f0f4e0, #e8eed0)",
                        borderRadius: "inherit",
                      }}
                    />
                  )}
                </div>
              </div>

              <div className="col-xs-12 col-sm-6 col-md-6 col-md-offset-1">
                <div className="about-additional-content service-content-card">
                  <h3 className="inherit-title">{title}</h3>

                  <div
                    className="about-additional-text"
                    dangerouslySetInnerHTML={{ __html: description || "" }}
                  />

                  <Link className="btn btn-primary" to={path}>
                    {moreText}
                  </Link>
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {canNotFindWhatIamLookingForSection && (
        <div className="b-ask">
          <div className="b-ask-content text-center">
            <h3 className="big-title-mod">
              {canNotFindWhatIamLookingForSection.title}
            </h3>

            <div className="b-text">
              <p
                dangerouslySetInnerHTML={{
                  __html: canNotFindWhatIamLookingForSection.description,
                }}
              />
            </div>

            <Link
              className="btn btn-primary"
              to={isAr ? "/ar/discuss-project" : "/discuss-project"}
            >
              {canNotFindWhatIamLookingForSection.askQuestionButton}
            </Link>
          </div>
        </div>
      )}
    </div>
  )
}

export default ServicesBox